import { useState, useMemo, useEffect, useCallback } from "react";

const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, Loader2, MessageSquareText, Eye, Hash, CheckCircle2, MapPin, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from "xlsx";

interface MessageTemplate {
  id: string;
  name: string;
  body: string;
}

const STARTER_TEMPLATES: MessageTemplate[] = [
  {
    id: "land-contract-offer",
    name: "Specialize (Land Contract)",
    body: "Contract Funding Group specializes in purchasing land contracts. We offer top cash pricing and fast funding. Contact me today for your cash offer.",
  },
  {
    id: "note-specialize",
    name: "Specialize (Owner-Financed Note)",
    body: "Contract Funding Group specializes in purchasing owner-financed notes. We offer top cash pricing and fast funding. Contact me today for your cash offer.",
  },
  {
    id: "document-type-intro",
    name: "Document Type Introduction",
    body: "I saw you recorded a {document_type} in {county} County in {recorded_date}. I buy these for cash — please call me to discuss your cash options.",
  },
  {
    id: "friendly-followup",
    name: "Friendly Follow-Up",
    body: "I recently sent you a letter about your land contract in {county} County. I'm still very interested in making you a fair cash offer. I know selling a note can be a big decision, so I'm happy to answer any questions.",
  },
  {
    id: "seasonal-greeting",
    name: "Seasonal Greeting + Offer",
    body: "Happy New Year! I saw your land contract filing in {county} County from {recorded_date} and wanted to reach out. If you've ever considered cashing out your payments, I'd love to make you an offer. No obligation — just a friendly conversation.",
  },
  {
    id: "still-interested",
    name: "Still Interested",
    body: "I'm still interested in making an offer for your {document_type} in {county} County. Please call me to review your cash options.",
  },
  {
    id: "top-cash",
    name: "Top Cash",
    body: "I noticed your {document_type} in {county} county. I pay top cash for the remaining payments. Give me a call to see what your payout looks like.",
  },
  {
    id: "lump-sum",
    name: "Lump Sum",
    body: "This is Mike. {county} county shows you as the seller on a {document_type}. I pay lump-sum cash for contracts like yours—give me a call anytime.",
  },
  {
    id: "texas-lump-sum",
    name: "Texas Lump-Sum",
    body: "This is Mike. {county} county shows you as the seller on an owner-financed note. I pay lump-sum for notes like yours—give me a call anytime.",
  },
  {
    id: "anytime",
    name: "Anytime",
    body: "My name is Mike. I pay cash for {document_type}s and offer strong lump‑sum prices. Call me anytime.",
  },
  {
    id: "quick-note",
    name: "Quick Note",
    body: "Just a quick note — I buy land contracts for cash. Happy to talk anytime. 517‑908‑0433.",
  },
];

const AVAILABLE_VARIABLES = [
  { token: "{first_name}", label: "First Name" },
  { token: "{last_name}", label: "Last Name" },
  { token: "{business_name}", label: "Business Name" },
  { token: "{county}", label: "County" },
  { token: "{recorded_date}", label: "Recorded Date" },
  { token: "{document_type}", label: "Document Type" },
  { token: "{state}", label: "State" },
  { token: "{origin_state}", label: "Origin State" },
  { token: "{consideration}", label: "Consideration ($)" },
  { token: "{property_address}", label: "Property Address" },
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return String(d.getFullYear());
}

function toProperCase(s: string | null): string {
  if (!s) return "";
  return s.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substring(1).toLowerCase());
}

function resolveDocumentLabel(record: any, override: string): string {
  if (override === "land_contract") return "land contract";
  if (override === "deed_of_trust") return "deed of trust";
  if (override === "owner_financed_note") return "owner-financed note";
  // auto: try to map DB value intelligently
  const raw = (record.document_type || "").toLowerCase().trim();
  if (raw.includes("land contract")) return "land contract";
  if (raw.includes("deed of trust")) return "deed of trust";
  if (raw.includes("note") || raw.includes("owner")) return "owner-financed note";
  return raw || "contract";
}

function buildMessage(template: string, record: any, documentLabelOverride: string = "auto"): string {
  return template
    .replace(/{first_name}/g, toProperCase(record.first_name))
    .replace(/{last_name}/g, toProperCase(record.last_name))
    .replace(/{business_name}/g, record.business_name || "")
    .replace(/{county}/g, toProperCase(record.county))
    .replace(/{recorded_date}/g, formatDate(record.recorded_date))
    .replace(/{document_type}/g, resolveDocumentLabel(record, documentLabelOverride))
    .replace(/{state}/g, (record.state || "").toUpperCase())
    .replace(/{origin_state}/g, (record.origin_state || "").toUpperCase())
    .replace(/{consideration}/g, record.consideration ? `$${Number(record.consideration).toLocaleString()}` : "")
    .replace(/{property_address}/g, record.property_address || "");
}

export function MailMergeExport() {
  const [templates] = useState<MessageTemplate[]>(STARTER_TEMPLATES);
  const [selectedTemplateId, setSelectedTemplateId] = useState(STARTER_TEMPLATES[0].id);
  const [customBody, setCustomBody] = useState("");
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewRecords, setPreviewRecords] = useState<any[]>([]);
  const [recordCount, setRecordCount] = useState<number | null>(null);
  const [counting, setCounting] = useState(false);
  const [commitDialogOpen, setCommitDialogOpen] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [mailDate, setMailDate] = useState(new Date().toISOString().split("T")[0]);
  const [committing, setCommitting] = useState(false);
  const [commitLimit, setCommitLimit] = useState<number | null>(null);

  // Filters
  const [stateFilter, setStateFilter] = useState("all");
  const [countyFilter, setCountyFilter] = useState<string[]>([]);
  const [countyOptions, setCountyOptions] = useState<string[]>([]);
  const [countySearch, setCountySearch] = useState("");
  const [timesMailedFilter, setTimesMailedFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [entityTypeFilter, setEntityTypeFilter] = useState("all");
  const [documentTypeFilter, setDocumentTypeFilter] = useState("all");
  const [maxRecordedDate, setMaxRecordedDate] = useState("");
  const [minRecordedDate, setMinRecordedDate] = useState("");
  const [maxLastMailedDate, setMaxLastMailedDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [responseFilter, setResponseFilter] = useState("no_response");
  const [documentLabelOverride, setDocumentLabelOverride] = useState("auto");
  const [splitMessage, setSplitMessage] = useState(true);
  const [campaignFilter, setCampaignFilter] = useState<string>("all");
  const [campaignOptions, setCampaignOptions] = useState<string[]>([]);
  const [campaignIds, setCampaignIds] = useState<string[] | null>(null);

  // Load distinct campaign names from mailing_history (paginated past Supabase 1k limit)
  useEffect(() => {
    (async () => {
      try {
        const names = new Set<string>();
        let from = 0;
        const pageSize = 1000;
        while (true) {
          const { data, error } = await supabase
            .from("mailing_history")
            .select("campaign_name")
            .order("campaign_name", { ascending: true })
            .range(from, from + pageSize - 1);
          if (error) throw error;
          if (!data || data.length === 0) break;
          data.forEach((r: any) => r.campaign_name && names.add(r.campaign_name));
          if (data.length < pageSize) break;
          from += pageSize;
        }
        setCampaignOptions(Array.from(names).sort());
      } catch (e) {
        console.error("Load campaigns failed:", e);
      }
    })();
  }, []);

  // Load distinct counties from mailing_records
  useEffect(() => {
    (async () => {
      try {
        const seen = new Map<string, string>();
        let from = 0;
        const pageSize = 1000;
        while (true) {
          const { data, error } = await supabase
            .from("mailing_records")
            .select("county")
            .not("county", "is", null)
            .range(from, from + pageSize - 1);
          if (error) throw error;
          if (!data || data.length === 0) break;
          data.forEach((r: any) => {
            if (r.county) {
              const key = r.county.trim().toUpperCase();
              if (key && !seen.has(key)) seen.set(key, r.county.trim());
            }
          });
          if (data.length < pageSize) break;
          from += pageSize;
        }
        setCountyOptions(Array.from(seen.values()).sort((a, b) => a.localeCompare(b)));
      } catch (e) {
        console.error("Load counties failed:", e);
      }
    })();
  }, []);



  // When campaign changes, fetch its record IDs via RPC
  useEffect(() => {
    (async () => {
      if (campaignFilter === "all") {
        setCampaignIds(null);
        return;
      }
      try {
        const { data, error } = await supabase.rpc("get_campaign_record_ids", { p_campaign: campaignFilter });
        if (error) throw error;
        const ids = (data || []).map((r: any) => r.record_id);
        setCampaignIds(ids);
      } catch (e) {
        console.error("Load campaign IDs failed:", e);
        setCampaignIds([]);
        toast.error("Failed to load campaign records");
      }
    })();
  }, [campaignFilter]);

  const activeTemplate = useMemo(() => {
    if (isCustomMode) return customBody;
    return templates.find((t) => t.id === selectedTemplateId)?.body || "";
  }, [isCustomMode, customBody, selectedTemplateId, templates]);

  const sampleRecord = {
    first_name: "BRIAN",
    last_name: "SMITH",
    county: "LOSCO",
    recorded_date: "2023-12-15",
    document_type: "LAND CONTRACT",
    state: "MI",
    origin_state: "MI",
    consideration: 85000,
    property_address: "123 Main St, Jackson, MI",
  };

  const sampleGreeting = useMemo(() => `Hi ${toProperCase(sampleRecord.first_name)} –`, []);
  const sampleMessage = useMemo(() => buildMessage(activeTemplate, sampleRecord, documentLabelOverride), [activeTemplate, documentLabelOverride]);

  const applyFilters = useCallback((query: any) => {
    if (statusFilter !== "all") {
      query = query.eq("mailing_status", statusFilter);
    }

    if (stateFilter !== "all") {
      query = query.eq("origin_state", stateFilter);
    }

    if (countyFilter.length > 0) {
      query = query.in("county", countyFilter);
    }

    if (timesMailedFilter === "0") {
      query = query.eq("times_mailed", 0);
    } else if (timesMailedFilter === "1") {
      query = query.eq("times_mailed", 1);
    } else if (timesMailedFilter === "2+") {
      query = query.gte("times_mailed", 2);
    }

    if (entityTypeFilter === "individual") {
      query = query.or("business_name.is.null,business_name.eq.");
    } else if (entityTypeFilter === "business") {
      query = query.not("business_name", "is", null).neq("business_name", "");
    }

    if (documentTypeFilter !== "all") {
      query = query.ilike("document_type", `%${documentTypeFilter}%`);
    }

    if (minRecordedDate) {
      query = query.not("recorded_date", "is", null).gte("recorded_date", minRecordedDate);
    }

    if (maxRecordedDate) {
      query = query.not("recorded_date", "is", null).lte("recorded_date", maxRecordedDate);
    }

    if (maxLastMailedDate) {
      // Include never-mailed records (null) plus those mailed on/before the cutoff
      query = query.or(`last_mailed_date.is.null,last_mailed_date.lte.${maxLastMailedDate}`);
    }

    if (responseFilter === "no_response") {
      query = query.or("response.is.null,response.eq.");
    } else if (responseFilter === "has_response") {
      query = query.not("response", "is", null).neq("response", "");
    }

    if (campaignIds !== null) {
      // Filter to records in selected campaign. Empty array → match none.
      if (campaignIds.length === 0) {
        query = query.eq("id", "00000000-0000-0000-0000-000000000000");
      } else {
        query = query.in("id", campaignIds);
      }
    }

    return query;
  }, [stateFilter, countyFilter, timesMailedFilter, statusFilter, entityTypeFilter, documentTypeFilter, minRecordedDate, maxRecordedDate, maxLastMailedDate, responseFilter, campaignIds]);

  const buildQuery = useCallback((selectFields: string) => {
    let query = supabase.from("mailing_records").select(selectFields);
    query = applyFilters(query);
    return query.order("last_name", { ascending: true }).order("first_name", { ascending: true });
  }, [applyFilters]);

  // Auto-count when filters change
  useEffect(() => {
    const fetchCount = async () => {
      setCounting(true);
      try {
        let query = supabase.from("mailing_records").select("id", { count: "exact", head: true });
        query = applyFilters(query);
        const { count, error } = await query;
        if (error) throw error;
        setRecordCount(count ?? 0);
      } catch (err) {
        console.error("Count failed:", err);
        setRecordCount(null);
      } finally {
        setCounting(false);
      }
    };

    const debounce = setTimeout(fetchCount, 400);
    return () => clearTimeout(debounce);
  }, [applyFilters]);

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const { data, error } = await buildQuery(
        "prefix, first_name, last_name, business_name, street_address, city, state, zip_code, county, recorded_date, document_type, origin_state, consideration, property_address, mailing_status"
      ).limit(5);
      if (error) throw error;
      setPreviewRecords(data || []);
      if (!data?.length) toast.info("No records match your filters");
    } catch (err) {
      console.error(err);
      toast.error("Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const handleExport = async () => {
    if (!activeTemplate.trim()) {
      toast.error("Please select or write a message template");
      return;
    }
    setExporting(true);
    try {
      let allRecords: any[] = [];
      let from = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await buildQuery(
          "seller_id, prefix, first_name, last_name, suffix, business_name, street_address, city, state, zip_code, county, recorded_date, document_type, origin_state, consideration, property_address, mailing_status"
        ).range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allRecords = [...allRecords, ...data];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allRecords.length === 0) {
        toast.error("No records match your filters");
        setExporting(false);
        return;
      }

      const rows = allRecords.map((r) => {
        const fullMessage = buildMessage(activeTemplate, r, documentLabelOverride);
        const lastNameRaw = r.last_name || "";
        const nameSuffix = lastNameRaw.match(/,\s*(Trustee|Successor Trustee)/i)?.[0] || r.suffix || "";
        const cleanLastName = lastNameRaw.replace(/,\s*(Trustee|Successor Trustee)/i, "").trim();
        
        const suffixClean = nameSuffix.replace(/^,\s*/, "");
        const prefix = r.prefix && r.prefix !== "explicitly_none" ? r.prefix : "";
        const addresseeParts = [prefix, toProperCase(r.first_name), toProperCase(cleanLastName)].filter(Boolean).join(" ");
        const addressee = suffixClean ? `${addresseeParts}, ${suffixClean}` : addresseeParts;
        
        // For business records, use business name as addressee
        const envelopeAddressee = r.business_name ? r.business_name : addressee;
        
        // Greeting: use first name for individuals, business name for businesses
        const greetingName = r.business_name 
          ? r.business_name 
          : toProperCase((r.first_name || "").split(/\s+/)[0]);
        
        const baseFields = {
          "Seller ID": r.seller_id || "",
          "Prefix": prefix,
          "First Name": toProperCase(r.first_name),
          "Last Name": toProperCase(cleanLastName),
          "Name Suffix": suffixClean,
          "Business Name": r.business_name || "",
          "Addressee": envelopeAddressee,
          "Street Address": r.street_address || "",
          "City": toProperCase(r.city),
          "State": (r.state || "").toUpperCase(),
          "Zip Code": (r.zip_code || "").split("-")[0],
          "County": toProperCase(r.county),
          "Greeting Name": greetingName,
          "Greeting": r.business_name ? `Dear ${r.business_name} –` : `Hi ${toProperCase((r.first_name || "").split(/\s+/)[0])} –`,
        };

        if (splitMessage) {
          const periodIdx = fullMessage.indexOf(". ");
          const line1 = periodIdx >= 0 ? fullMessage.slice(0, periodIdx + 1) : fullMessage;
          const line2 = periodIdx >= 0 ? fullMessage.slice(periodIdx + 2) : "";
          return { ...baseFields, "Message Line 1": line1, "Message Line 2": line2 };
        } else {
          return { ...baseFields, "Message": fullMessage };
        }
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = splitMessage
        ? [
            { wch: 20 }, { wch: 18 }, { wch: 22 }, { wch: 14 }, { wch: 32 }, { wch: 30 }, { wch: 18 },
            { wch: 8 }, { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 60 }, { wch: 60 },
          ]
        : [
            { wch: 20 }, { wch: 18 }, { wch: 22 }, { wch: 14 }, { wch: 32 }, { wch: 30 }, { wch: 18 },
            { wch: 8 }, { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 80 },
          ];

      XLSX.utils.book_append_sheet(wb, ws, "Mail Merge");
      XLSX.writeFile(wb, `Mail_Merge_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success(`Exported ${rows.length} records with personalized messages`);
    } catch (err) {
      console.error(err);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleCommitToMail = async () => {
    if (!campaignName.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    setCommitting(true);
    try {
      // Fetch all matching record IDs with their current times_mailed
      // Use a direct query without ordering (not needed for commit) to avoid column selection issues
      let allRecords: { id: string; times_mailed: number }[] = [];
      let from = 0;
      const pageSize = 1000;

      while (true) {
        let query = supabase.from("mailing_records").select("id, times_mailed");
        query = applyFilters(query);
        query = query
          .order("last_name", { ascending: true })
          .order("first_name", { ascending: true });
        const { data, error } = await query.range(from, from + pageSize - 1);
        if (error) {
          console.error("Commit fetch error:", error);
          throw error;
        }
        if (!data || data.length === 0) break;
        allRecords = [...allRecords, ...data.map((r: any) => ({ id: r.id, times_mailed: r.times_mailed || 0 }))];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allRecords.length === 0) {
        toast.error("No records match your filters");
        setCommitting(false);
        return;
      }

      // Apply optional limit
      if (commitLimit && commitLimit > 0 && commitLimit < allRecords.length) {
        allRecords = allRecords.slice(0, commitLimit);
      }

      // Process in batches of 500
      const batchSize = 500;
      for (let i = 0; i < allRecords.length; i += batchSize) {
        const batch = allRecords.slice(i, i + batchSize);

        // Insert mailing history entries with correct mailing_round per record
        const historyEntries = batch.map((rec) => ({
          mailing_record_id: rec.id,
          campaign_name: campaignName.trim(),
          mailed_date: mailDate,
          mailing_round: rec.times_mailed + 1,
        }));

        const { error: historyError } = await supabase
          .from("mailing_history")
          .insert(historyEntries);
        if (historyError) throw historyError;

        // Update mailing records
        for (const rec of batch) {
          const { error: updateError } = await supabase
            .from("mailing_records")
            .update({
              times_mailed: rec.times_mailed + 1,
              last_mailed_date: mailDate,
            })
            .eq("id", rec.id);
          if (updateError) throw updateError;
        }
      }

      toast.success(`Committed ${allRecords.length} records to "${campaignName.trim()}"`);
      setCommitDialogOpen(false);
      setCampaignName("");
    } catch (err) {
      console.error(err);
      toast.error("Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const insertVariable = (token: string) => {
    setCustomBody((prev) => prev + token);
    setIsCustomMode(true);
  };

  return (
    <div className="space-y-6">
      {/* Template Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquareText className="h-5 w-5" />
            Message Template
          </CardTitle>
          <CardDescription>
            Choose a starter template or write your own. Variables like {"{county}"} get replaced with each record's actual data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label>Template</Label>
              <Select
                value={isCustomMode ? "__custom__" : selectedTemplateId}
                onValueChange={(v) => {
                  if (v === "__custom__") {
                    setIsCustomMode(true);
                  } else {
                    setIsCustomMode(false);
                    setSelectedTemplateId(v);
                  }
                }}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">✏️ Write Custom Message</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isCustomMode && (
            <div className="space-y-2">
              <Label>Custom Message</Label>
              <Textarea
                value={customBody}
                onChange={(e) => setCustomBody(e.target.value)}
                placeholder="Write your message here... Use variables like {county} to personalize."
                className="min-h-[100px] font-mono text-sm"
              />
              <div className="flex flex-wrap gap-1.5">
                <span className="text-xs text-muted-foreground mr-1 self-center">Insert:</span>
                {AVAILABLE_VARIABLES.map((v) => (
                  <Badge
                    key={v.token}
                    variant="outline"
                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors text-xs"
                    onClick={() => insertVariable(v.token)}
                  >
                    {v.label}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Export Options */}
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <Checkbox
                id="split-message"
                checked={splitMessage}
                onCheckedChange={(checked) => setSplitMessage(checked === true)}
              />
              <Label htmlFor="split-message" className="text-sm cursor-pointer">
                Split message into two columns
              </Label>
            </div>
            <div className="w-64">
              <Label className="text-xs">Document Label in Message</Label>
              <Select value={documentLabelOverride} onValueChange={setDocumentLabelOverride}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (use database value)</SelectItem>
                  <SelectItem value="land_contract">Land Contract</SelectItem>
                  <SelectItem value="deed_of_trust">Deed of Trust</SelectItem>
                  <SelectItem value="owner_financed_note">Owner-Financed Note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Overrides the {"{document_type}"} variable in all messages. Use "Land Contract" for MI/OH mailings.
            </p>
          </div>

          {/* Live Preview */}
          <div className="rounded-lg border bg-muted/40 p-4">
            <p className="text-xs font-medium text-muted-foreground mb-2">Live Preview (sample data):</p>
            <p className="text-sm font-medium mb-1">{sampleGreeting}</p>
            <p className="text-sm italic leading-relaxed">{sampleMessage || "Select a template to see a preview..."}</p>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Filter Records</CardTitle>
              <CardDescription>Choose which records to include in the export</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-muted-foreground" />
              {counting ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <span className="text-2xl font-bold tabular-nums">
                  {recordCount !== null ? recordCount.toLocaleString() : "—"}
                </span>
              )}
              <span className="text-sm text-muted-foreground">records</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="col-span-2 md:col-span-3 lg:col-span-4">
              <Label className="text-xs">Campaign</Label>
              <Select
                value={campaignFilter}
                onValueChange={(value) => {
                  setCampaignFilter(value);
                  if (value !== "all") setMaxLastMailedDate("");
                }}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All Campaigns (no filter)</SelectItem>
                  {campaignOptions.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {campaignFilter !== "all" && campaignIds !== null && (
                <p className="text-xs text-muted-foreground mt-1">
                  Loaded {campaignIds.length.toLocaleString()} records from "{campaignFilter}". Other filters still apply.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Origin State</Label>
              <Select value={stateFilter} onValueChange={setStateFilter}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">All Origin States</SelectItem>
                  {US_STATES.map(({ code, name }) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">County</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 w-[180px] justify-between font-normal">
                    <span className="truncate">
                      {countyFilter.length === 0
                        ? "All Counties"
                        : countyFilter.length === 1
                        ? countyFilter[0]
                        : `${countyFilter.length} counties`}
                    </span>
                    <MapPin className="h-3 w-3 ml-1 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-2" align="start">
                  <Input
                    autoFocus
                    placeholder="Search counties..."
                    value={countySearch}
                    onChange={(e) => setCountySearch(e.target.value)}
                    className="h-8 mb-2"
                  />
                  <div className="flex items-center justify-between mb-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setCountyFilter([])}
                    >
                      Clear all
                    </Button>
                    {countyFilter.length > 0 && (
                      <span className="text-xs text-muted-foreground">{countyFilter.length} selected</span>
                    )}
                  </div>
                  <div className="max-h-[280px] overflow-auto space-y-0.5">
                    {countyOptions
                      .filter((c) => c.toLowerCase().includes(countySearch.toLowerCase()))
                      .map((c) => (
                        <label
                          key={c}
                          className="flex items-center gap-2 px-2 py-1 hover:bg-muted rounded cursor-pointer text-xs"
                        >
                          <Checkbox
                            checked={countyFilter.includes(c)}
                            onCheckedChange={(checked) =>
                              setCountyFilter((prev) =>
                                checked ? [...prev, c] : prev.filter((x) => x !== c)
                              )
                            }
                          />
                          <span>{c}</span>
                        </label>
                      ))}
                    {countyOptions.length === 0 && (
                      <p className="text-xs text-muted-foreground p-2">Loading counties...</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              {countyFilter.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {countyFilter.map((c) => (
                    <Badge key={c} variant="secondary" className="text-xs gap-1 pr-1">
                      {c}
                      <button
                        onClick={() => setCountyFilter((prev) => prev.filter((x) => x !== c))}
                        className="hover:bg-muted-foreground/20 rounded"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Times Mailed</Label>
              <Select value={timesMailedFilter} onValueChange={setTimesMailedFilter}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="0">Never Mailed</SelectItem>
                  <SelectItem value="1">Mailed Once</SelectItem>
                  <SelectItem value="2+">Mailed 2+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="responded">Responded</SelectItem>
                  <SelectItem value="temporarily_away">Temporarily Away</SelectItem>
                  <SelectItem value="do_not_mail">Do Not Mail</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Entity Type</Label>
              <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="individual">Individuals Only</SelectItem>
                  <SelectItem value="business">Businesses Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Document Type</Label>
              <Select value={documentTypeFilter} onValueChange={setDocumentTypeFilter}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="LAND CONTRACT">Land Contract</SelectItem>
                  <SelectItem value="MEMORANDUM">Memorandum</SelectItem>
                  <SelectItem value="MORTGAGE">Mortgage</SelectItem>
                  <SelectItem value="DEED">Deed</SelectItem>
                  <SelectItem value="ASSIGNMENT">Assignment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Recorded After</Label>
              <Input
                type="date"
                value={minRecordedDate}
                onChange={(e) => setMinRecordedDate(e.target.value)}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Recorded On or Before</Label>
              <Input
                type="date"
                value={maxRecordedDate}
                onChange={(e) => setMaxRecordedDate(e.target.value)}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Last Mailed On or Before</Label>
              <Input
                type="date"
                value={maxLastMailedDate}
                onChange={(e) => setMaxLastMailedDate(e.target.value)}
              className="h-9"
            />
            </div>
            <div>
              <Label className="text-xs">Response Status</Label>
              <Select value={responseFilter} onValueChange={setResponseFilter}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="no_response">No Response Yet</SelectItem>
                  <SelectItem value="has_response">Has Responded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview + Export */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-3 items-center flex-wrap">
            <Button variant="outline" onClick={handlePreview} disabled={previewing} className="gap-2">
              {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              Preview 5 Records
            </Button>
            <Button onClick={handleExport} disabled={exporting || recordCount === 0} className="gap-2">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {exporting ? "Exporting..." : `Download Mail Merge Excel${recordCount !== null ? ` (${recordCount.toLocaleString()})` : ""}`}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setCommitDialogOpen(true)}
              disabled={recordCount === 0}
              className="gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              Commit to Mail{recordCount !== null ? ` (${recordCount.toLocaleString()})` : ""}
            </Button>
          </div>

          {previewRecords.length > 0 && (
            <div className="mt-6 space-y-3">
              <p className="text-sm font-medium">Preview — First 5 Records:</p>
              {previewRecords.map((r, i) => (
                <div key={i} className="rounded-lg border p-3 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">
                    {toProperCase(r.first_name)} {toProperCase(r.last_name)} — {toProperCase(r.city)}, {(r.state || "").toUpperCase()}
                  </p>
                  <p className="text-sm font-medium mt-1">Hi {toProperCase(r.first_name)} –</p>
                  <p className="text-sm leading-relaxed">{buildMessage(activeTemplate, r, documentLabelOverride)}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Commit to Mail Dialog */}
      <Dialog open={commitDialogOpen} onOpenChange={setCommitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Commit to Mail</DialogTitle>
            <DialogDescription>
            This will mark {commitLimit && commitLimit > 0 ? `first ${commitLimit.toLocaleString()} of` : ""} {recordCount?.toLocaleString() ?? "—"} records as mailed, increment their mail count, and create history entries.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Campaign Name</Label>
              <Input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g. March 2026 – MI Land Contracts"
              />
            </div>
            <div>
              <Label>Mail Date</Label>
              <Input
                type="date"
                value={mailDate}
                onChange={(e) => setMailDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Limit (optional)</Label>
              <Input
                type="number"
                min={1}
                value={commitLimit || ""}
                onChange={(e) => setCommitLimit(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Leave blank to commit all records"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommitDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCommitToMail} disabled={committing} className="gap-2">
              {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {committing ? "Committing..." : `Commit ${(commitLimit && commitLimit > 0 && recordCount && commitLimit < recordCount ? commitLimit : recordCount)?.toLocaleString() ?? ""} Records`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
